import useTasksStats from '@/components/deal/tasks/hooks/use-tasks-stats';
import TaskDialog from '@/components/deal/tasks/task-dialog';
import TasksFraction from '@/components/deal/tasks/tasks-fraction';
import TasksProgressRing from '@/components/delivery-board-from-router/_components/tasks-progress-ring';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import useTasks from '@/hooks/use-tasks';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { dealStore, taskStore } from '@/models/stores';
import type { AssignedTask, Task as TaskType } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Landmark, Plus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../../ui/button';
import { ComboboxPopover } from '../../ui/combobox/combobox-popover';

const DealTaskSelector = ({
  dealId,
  withHokeyListener = false,
}: {
  dealId: string;
  withHokeyListener?: boolean;
}) => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const assignedTasks = deal?.assigned_tasks || [];
  const { tasks } = useTasks();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [comboBoxPopoverIsOpen, setComboBoxPopoverIsOpen] = useState(false);

  const taskOptions = useMemo(() => {
    const options = [];
    for (const task of tasks) {
      options.push({
        value: task.id,
        id: task.id,
        label: task.title,
        description: task.description,
        icon: <Landmark className="h-3.5 w-3.5" />,
      });
    }
    return options;
  }, [tasks]);

  const queryClient = useQueryClient();

  const [titleValue, setTitleValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');

  const createTaskMutation = useMutation({
    onMutate: async ({ task }) => {
      taskStore.add(task);
    },
    mutationFn: async ({ task }: { task: TaskType }) => {
      const response = await pdkAxios
        .post(`/tasks/create`, {
          title: task.title,
          description: task.description,
        })
        .catch((e) => {
          console.error('Error creating task', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (_data, { task }) => {
      setDialogIsOpen(false);
      setTitleValue('');
      setDescriptionValue('');

      // UX NOTE: we want to assign the task to the deal immediately after it is created
      // so that the user can see the task in the deal immediately
      assignTaskMutation.mutate({
        task: {
          id: uuidv4(),
          task_id: task.id,
          deal_id: dealId,
          title: task.title,
          description: task.description,
          status: 'incomplete',
        },
      });
    },
    onError: (error) => {
      console.error('Failed to create task', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const assignTaskMutation = useMutation({
    onMutate: async ({ task }: { task: AssignedTask }) => {
      dealStore.upsertAssignedTask({
        assignedTask: {
          id: uuidv4(),
          task_id: task.task_id,
          deal_id: dealId,
          title: task.title,
          description: task.description,
          status: 'incomplete',
        },
        id: dealId,
      });
    },
    mutationFn: async ({ task }: { task: AssignedTask }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/tasks/assign`, {
          id: task.id,
          task_id: task.task_id,
          deal_id: dealId,
          title: task.title,
          description: task.description,
          status: 'incomplete',
        })
        .catch((e) => {
          console.error('Error assigning task', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (_data) => {
      setDialogIsOpen(false);

      // TODO: add task to the deal
    },
    onError: (error, variables) => {
      console.error('Failed to assign task', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const handleAssignTask = (_value: string) => {
    toast({
      title: 'Feature not implemented yet',
      description:
        "Slack us if you want the 'trade-in appraisal assign task' feature",
    });
    return;

    // const innerTask = tasks.find((task: Task) => task.id === value);
    // if (!innerTask) {
    //   return;
    // } else {
    //   assignTaskMutation.mutate({
    //     task: {
    //       id: innerTask.id,
    //       title: innerTask.title,
    //       description: innerTask.description,
    //     },
    //   });
    // }
  };

  const { hasAssignedTasks } = useTasksStats(assignedTasks);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const onHotkeyPressed = useCallback(() => {
    buttonRef.current?.click();
  }, []);

  return (
    <div
      className={cn('flex flex-row gap-2 w-full px-2', {
        'justify-between': hasAssignedTasks,
      })}
    >
      {hasAssignedTasks && (
        <div className="flex flex-row gap-2 items-center">
          <div className="text-xs  text-gray-500 text-nowrap">Tasks</div>
          <TasksFraction assignedTasks={assignedTasks} />
          <TasksProgressRing assignedTasks={assignedTasks} />
        </div>
      )}
      <div>
        <ComboboxPopover
          open={comboBoxPopoverIsOpen}
          onOpenChange={setComboBoxPopoverIsOpen}
          endAdornment={
            <KeyboardKeyIcon>{HOTKEYS.DEAL_ADD_TASK}</KeyboardKeyIcon>
          }
          placeholder="Set task..."
          onChange={handleAssignTask}
          emptyMessage="No task found"
          options={taskOptions}
          footerOptions={
            <CommandGroup>
              <p className="p-2 rounded text-xs  text-gray-600">New task</p>
              <CommandItem
                onSelect={() => {
                  toast({
                    title: 'Feature not implemented yet',
                    description:
                      "Slack us if you want the 'trade-in appraisal create task' feature",
                  });
                  return;
                  // setComboBoxPopoverIsOpen(false);
                  // setDialogIsOpen(true);
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                <p className="rounded text-xs  text-gray-600">New task</p>
              </CommandItem>
            </CommandGroup>
          }
          side={hasAssignedTasks ? 'left' : 'right'}
          align="start"
        >
          <div>
            <Button variant="ghost" className="px-0 py-1 h-fit" ref={buttonRef}>
              {!hasAssignedTasks ? (
                <div className="flex flex-row gap-1 items-center">
                  <Plus
                    className={cn(
                      'h-3.5 w-3.5 cursor-pointer text-gray-500',
                      {},
                    )}
                  />
                  <div className="text-xs  text-gray-500 text-nowrap">
                    Add task
                  </div>
                </div>
              ) : (
                <Plus
                  className={cn('h-3.5 w-3.5 cursor-pointer text-gray-500', {})}
                />
              )}
            </Button>

            {withHokeyListener && (
              <HotKeyListener
                hotkeys={HOTKEYS.DEAL_ADD_TASK}
                onHotkeyPressed={onHotkeyPressed}
              />
            )}
          </div>
        </ComboboxPopover>

        <TaskDialog
          titleValue={titleValue}
          setTitleValue={setTitleValue}
          descriptionValue={descriptionValue}
          setDescriptionValue={setDescriptionValue}
          onClickCta={({ titleValue, descriptionValue }) => {
            createTaskMutation.mutate({
              task: {
                id: uuidv4(),
                title: titleValue,
                description: descriptionValue,
              },
            });
          }}
          ctaText={'Create Task'}
          onOpen={() => setDialogIsOpen(true)}
          onClose={() => setDialogIsOpen(false)}
          dialogIsOpen={dialogIsOpen}
        ></TaskDialog>
      </div>
    </div>
  );
};

export default DealTaskSelector;
